import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../lib/AuthContext';
import { authApi } from '../lib/api';
import { trackLogin, trackSignUp } from '../lib/analytics';
import { Mail, Lock, User, ArrowRight, Eye, EyeOff, Zap, Loader2, CheckCircle, ArrowLeft } from 'lucide-react';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
// THIS BREAKS THE AUTH. The redirect must be derived from window.location.origin.
const startGoogleAuth = () => {
  const redirectUrl = window.location.origin + '/auth/callback';
  window.location.href =
    'https://auth.emergentagent.com/?redirect=' + encodeURIComponent(redirectUrl);
};

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
    <path
      d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"
      fill="#4285F4"
    />
    <path
      d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.859-3.048.859-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"
      fill="#34A853"
    />
    <path
      d="M3.964 10.705A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.705V4.963H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.037l3.007-2.332z"
      fill="#FBBC05"
    />
    <path
      d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.963L3.964 7.295C4.672 5.168 6.656 3.58 9 3.58z"
      fill="#EA4335"
    />
  </svg>
);

export default function AuthPage() {
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      if (mode === 'forgot') {
        await authApi.forgotPassword(email);
        setResetSent(true);
      } else if (mode === 'login') {
        await login(email, password);
        trackLogin('email');
        navigate('/account');
      } else {
        await register(email, password, firstName, lastName);
        trackSignUp('email');
        navigate('/account');
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const switchMode = (m) => {
    setMode(m);
    setError('');
    setResetSent(false);
  };

  return (
    <main className="min-h-screen bg-black flex items-center justify-center px-4 py-20">
      <div className="w-full max-w-md">
        <h1 className="sr-only">Sign In or Create Account — NinjAthlete</h1>
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 justify-center mb-10" data-testid="auth-logo">
          <Zap className="w-5 h-5 text-[#e8ff00]" />
          <span className="text-white text-lg tracking-widest uppercase" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>NinjAthlete</span>
        </Link>

        {mode === 'forgot' ? (
          /* Forgot Password View */
          <div data-testid="forgot-password-form">
            <button
              onClick={() => switchMode('login')}
              className="flex items-center gap-1 text-zinc-500 hover:text-zinc-300 text-sm mb-6 transition-colors"
              data-testid="back-to-login"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Back to sign in
            </button>

            <h2 className="text-white text-2xl mb-2" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Reset Password</h2>
            <p className="text-zinc-500 text-sm mb-6">Enter your email and we'll send you a reset link.</p>

            {resetSent ? (
              <div className="text-center py-8" data-testid="reset-email-sent">
                <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                <h3 className="text-white text-lg mb-2" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Check Your Email</h3>
                <p className="text-zinc-400 text-sm mb-6">If an account exists for <span className="text-white">{email}</span>, we've sent a password reset link.</p>
                <button onClick={() => switchMode('login')} className="text-[#e8ff00] text-sm hover:underline">
                  Return to Sign In
                </button>
              </div>
            ) : (
              <>
                {error && (
                  <div className="bg-red-500/10 border border-red-500/30 text-red-400 text-sm px-4 py-3 mb-6" data-testid="auth-error">{error}</div>
                )}
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <input type="email" value={email} onChange={e => setEmail(e.target.value)} required
                      placeholder="Email address" className="w-full pl-10 pr-4 py-3 bg-zinc-900 border border-zinc-700 text-white text-sm placeholder-zinc-500 focus:border-[#e8ff00] focus:outline-none transition-colors"
                      data-testid="forgot-email" />
                  </div>
                  <button type="submit" disabled={submitting}
                    className="w-full py-3 bg-[#e8ff00] text-black text-sm uppercase tracking-wider hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
                    style={{ fontFamily: 'Bebas Neue, sans-serif' }}
                    data-testid="forgot-submit-btn">
                    {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending...</> : <>Send Reset Link <ArrowRight className="w-4 h-4" /></>}
                  </button>
                </form>
              </>
            )}
          </div>
        ) : (
          /* Login / Register View */
          <>
            <div className="flex border-b border-zinc-800 mb-8">
              {['login', 'register'].map(m => (
                <button
                  key={m}
                  onClick={() => switchMode(m)}
                  className={`flex-1 pb-3 text-sm uppercase tracking-wider transition-colors ${
                    mode === m ? 'text-[#e8ff00] border-b-2 border-[#e8ff00]' : 'text-zinc-500 hover:text-zinc-300'
                  }`}
                  style={{ fontFamily: 'Bebas Neue, sans-serif' }}
                  data-testid={`auth-tab-${m}`}
                >
                  {m === 'login' ? 'Sign In' : 'Create Account'}
                </button>
              ))}
            </div>

            {error && (
              <div className="bg-red-500/10 border border-red-500/30 text-red-400 text-sm px-4 py-3 mb-6" data-testid="auth-error">{error}</div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5" data-testid="auth-form">
              {mode === 'register' && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)}
                      placeholder="First name" className="w-full pl-10 pr-4 py-3 bg-zinc-900 border border-zinc-700 text-white text-sm placeholder-zinc-500 focus:border-[#e8ff00] focus:outline-none transition-colors"
                      data-testid="auth-first-name" />
                  </div>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <input type="text" value={lastName} onChange={e => setLastName(e.target.value)}
                      placeholder="Last name" className="w-full pl-10 pr-4 py-3 bg-zinc-900 border border-zinc-700 text-white text-sm placeholder-zinc-500 focus:border-[#e8ff00] focus:outline-none transition-colors"
                      data-testid="auth-last-name" />
                  </div>
                </div>
              )}

              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} required
                  placeholder="Email address" className="w-full pl-10 pr-4 py-3 bg-zinc-900 border border-zinc-700 text-white text-sm placeholder-zinc-500 focus:border-[#e8ff00] focus:outline-none transition-colors"
                  data-testid="auth-email" />
              </div>

              <div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <input type={showPass ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} required
                    placeholder="Password" minLength={6}
                    className="w-full pl-10 pr-12 py-3 bg-zinc-900 border border-zinc-700 text-white text-sm placeholder-zinc-500 focus:border-[#e8ff00] focus:outline-none transition-colors"
                    data-testid="auth-password" />
                  <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300 transition-colors">
                    {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {mode === 'login' && (
                  <button
                    type="button"
                    onClick={() => switchMode('forgot')}
                    className="text-zinc-500 hover:text-[#e8ff00] text-xs mt-2 transition-colors"
                    data-testid="forgot-password-link"
                  >
                    Forgot password?
                  </button>
                )}
              </div>

              <button type="submit" disabled={submitting}
                className="w-full py-3 bg-[#e8ff00] text-black text-sm uppercase tracking-wider hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
                style={{ fontFamily: 'Bebas Neue, sans-serif' }}
                data-testid="auth-submit-btn">
                {/* eslint-disable-next-line no-nested-ternary -- readable tier picker */}
                {submitting ? 'Please wait...' : mode === 'login' ? 'Sign In' : 'Create Account'}
                <ArrowRight className="w-4 h-4" />
              </button>
            </form>

            {mode !== 'forgot' && (
              <>
                <div
                  className="flex items-center gap-3 my-6"
                  data-testid="auth-google-divider"
                >
                  <div className="flex-1 h-px bg-zinc-800" />
                  <span className="text-zinc-600 text-xs uppercase tracking-widest">or</span>
                  <div className="flex-1 h-px bg-zinc-800" />
                </div>
                <button
                  type="button"
                  onClick={startGoogleAuth}
                  disabled={submitting}
                  className="w-full py-3 bg-white text-black text-sm font-medium hover:bg-zinc-100 transition-colors disabled:opacity-50 flex items-center justify-center gap-3"
                  data-testid="auth-google-btn"
                >
                  <GoogleIcon />
                  Continue with Google
                </button>
              </>
            )}

            <p className="text-zinc-600 text-xs text-center mt-8">
              {mode === 'login'
                ? <>Don't have an account? <button onClick={() => switchMode('register')} className="text-[#e8ff00] hover:underline">Create one</button></>
                : <>Already have an account? <button onClick={() => switchMode('login')} className="text-[#e8ff00] hover:underline">Sign in</button></>
              }
            </p>
          </>
        )}
      </div>
    </main>
  );
}
